import axios from 'axios';
import { resolveAuthApiUrl } from './resolve-auth-api-url';
import { withSocialChannelHeaders } from './social-channel';
import { parseSocialIntent } from './social-intent';

const SOCIAL_SESSION_LOGIN_PATH = '/api/social/v1/session/login';

export interface BeginSocialSessionInput {
  attemptId: string;
  callback: string;
  codeChallenge: string;
  intent?: string;
  password: string;
  remember: boolean;
  state: string;
  username: string;
}

export interface SocialSessionGrant {
  attemptId: string;
  callback: string;
  code: string;
  state: string;
}

export interface SocialSessionRepository {
  beginSession(input: BeginSocialSessionInput): Promise<SocialSessionGrant>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseGrant(value: unknown): SocialSessionGrant {
  if (
    !isRecord(value)
    || typeof value.attempt_id !== 'string'
    || typeof value.callback !== 'string'
    || typeof value.code !== 'string'
    || typeof value.state !== 'string'
  ) {
    throw new Error('Invalid social session grant.');
  }

  return {
    attemptId: value.attempt_id,
    callback: value.callback,
    code: value.code,
    state: value.state,
  };
}

export function useSocialSessionRepository(): SocialSessionRepository {
  const beginSession = async (input: BeginSocialSessionInput): Promise<SocialSessionGrant> => {
    const response = await axios.post<unknown>(
      resolveAuthApiUrl(SOCIAL_SESSION_LOGIN_PATH),
      {
        attempt_id: input.attemptId,
        callback: input.callback,
        code_challenge: input.codeChallenge,
        password: input.password,
        remember: input.remember,
        state: input.state,
        username: input.username,
      },
      {
        headers: withSocialChannelHeaders({
          'Content-Type': 'application/json',
          ...(parseSocialIntent(input.intent) ? { 'X-OpenReferences-Intent': input.intent } : {}),
        }, true),
        withCredentials: true,
      },
    );

    return parseGrant(response.data);
  };

  return { beginSession };
}
